var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Auth0Client } from "@auth0/nextjs-auth0/server";
import { firstValueFrom, from } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { isLocalDev } from "./is-local-dev";
import { getPublicRuntimeConfig, getServerRuntimeConfig, } from "./runtime-config";
const serverRuntimeConfig = getServerRuntimeConfig();
// Helper function to create Observable from fetch
const createRequest = (url, options) => {
    return from(fetch(url, options));
};
export const getEnvAccessToken = () => {
    var _a, _b;
    let authUrl = (_a = serverRuntimeConfig.DTN_AUTH_URL) !== null && _a !== void 0 ? _a : "https://api.auth.dtn.com";
    let audienceUrl = (_b = serverRuntimeConfig.DTN_IDENTITY_AUDIENCE) !== null && _b !== void 0 ? _b : "https://identity.auth.dtn.com";
    // Check if required config is available
    if (!serverRuntimeConfig.DTN_CLIENT_ID ||
        !serverRuntimeConfig.DTN_CLIENT_SECRET) {
        // eslint-disable-next-line no-console
        console.error("Missing required DTN_CLIENT_ID or DTN_CLIENT_SECRET in serverRuntimeConfig");
        throw new Error("Missing required DTN authentication configuration");
    }
    return createRequest(`${authUrl}/v1/tokens/authorize`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "content-type": "application/json",
        },
        body: JSON.stringify({
            grant_type: "client_credentials",
            client_id: serverRuntimeConfig.DTN_CLIENT_ID,
            client_secret: serverRuntimeConfig.DTN_CLIENT_SECRET,
            audience: audienceUrl,
        }),
    }).pipe(switchMap((response) => {
        if (!response.ok) {
            // Try to get error details from response
            return from(response.text()).pipe(switchMap((errorText) => {
                // eslint-disable-next-line no-console
                console.error("Auth service error response:", errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}. Response: ${errorText}`);
            }));
        }
        return from(response.json());
    }), map((data) => {
        if (data === null || data === undefined || typeof data !== "object") {
            // eslint-disable-next-line no-console
            console.error("Invalid response structure:", data);
            throw new Error(`Invalid response from auth service - invalid data structure. Response: ${JSON.stringify(data)}`);
        }
        // Check if access_token is at root level (direct response)
        if ("access_token" in data &&
            typeof data.access_token === "string") {
            return data;
        }
        // Check if access_token is nested inside data property
        if ("data" in data &&
            typeof data.data === "object" &&
            data.data !== null) {
            const nestedData = data.data;
            if ("access_token" in nestedData &&
                typeof nestedData.access_token === "string" &&
                "expires_in" in nestedData &&
                typeof nestedData.expires_in === "number" &&
                "token_type" in nestedData &&
                typeof nestedData.token_type === "string") {
                const result = {
                    access_token: nestedData.access_token,
                    expires_in: nestedData.expires_in,
                    token_type: nestedData.token_type,
                };
                return result;
            }
        }
        // eslint-disable-next-line no-console
        console.error("Invalid response structure:", data);
        throw new Error(`Invalid response from auth service - missing access_token. Response: ${JSON.stringify(data)}`);
    }), catchError((error) => {
        // eslint-disable-next-line no-console
        console.error("Error getting access token:", error);
        throw error; // Re-throw instead of returning EMPTY
    }));
};
const getAuthData = (accessResponse) => {
    var _a;
    const { DTN_PRODUCT_CODE } = getPublicRuntimeConfig();
    if (DTN_PRODUCT_CODE === undefined) {
        // eslint-disable-next-line no-console
        console.error("Missing DTN_PRODUCT_CODE in publicRuntimeConfig");
        throw new Error("Missing DTN_PRODUCT_CODE configuration");
    }
    let identityHost = (_a = serverRuntimeConfig.DTN_IDENTITY_HOST) !== null && _a !== void 0 ? _a : "https://identity.auth.dtn.com";
    // request identity endpoint with access token and get authData
    return createRequest(`${identityHost}/v1/products/${DTN_PRODUCT_CODE}/auth-info`, {
        method: "GET",
        headers: {
            Accept: "application/json",
            "content-type": "application/json",
            Authorization: `Bearer ${accessResponse.access_token}`,
        },
    }).pipe(switchMap((response) => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        return from(response.json());
    }), map((data) => {
        if (data === null ||
            data === undefined ||
            typeof data !== "object" ||
            !("data" in data) ||
            typeof data.data !== "object" ||
            data.data === null ||
            data.data === undefined) {
            throw new Error("Invalid response from identity service - missing data");
        }
        const authData = data;
        if (!authData.data.auth0_issuer_base_url ||
            !authData.data.client_id ||
            !authData.data.client_secret) {
            throw new Error("Invalid auth data - missing required fields");
        }
        return authData;
    }), catchError((error) => {
        // eslint-disable-next-line no-console
        console.error("Error getting auth data:", error);
        throw error; // Re-throw instead of returning EMPTY
    }));
};
const getHostHeader = (req) => {
    var _a;
    if (!req)
        return undefined;
    if ("headers" in req && typeof req.headers.get === "function") {
        return (_a = req.headers.get("host")) !== null && _a !== void 0 ? _a : undefined;
    }
    if ("headers" in req) {
        const headers = req.headers;
        return typeof headers.host === "string" ? headers.host : undefined;
    }
    return undefined;
};
const buildBaseUrlFromRequest = (req) => {
    const configuredBase = serverRuntimeConfig.DTN_APP_DOMAIN;
    const baseDomainHost = (() => {
        try {
            return new URL(configuredBase).hostname;
        }
        catch (_a) {
            return undefined;
        }
    })();
    if (req === undefined || baseDomainHost === undefined) {
        return configuredBase;
    }
    const requestHostHeader = getHostHeader(req);
    if (typeof requestHostHeader !== "string" || requestHostHeader.length === 0) {
        return configuredBase;
    }
    const isLocal = isLocalDev(requestHostHeader);
    if (requestHostHeader === baseDomainHost ||
        requestHostHeader.endsWith(`.${baseDomainHost}`)) {
        const proto = isLocal ? "http" : "https";
        return `${proto}://${requestHostHeader}`;
    }
    return configuredBase;
};
// =============================================================================
// AUTH0 CLIENT INITIALIZATION (V4)
// =============================================================================
const initAuth = (baseURL) => {
    var _a;
    const audienceUrl = (_a = serverRuntimeConfig.DTN_AUTH_AUDIENCE) !== null && _a !== void 0 ? _a : "https://api.auth.dtn.com";
    // Calculate cookie domain for cross-subdomain sessions
    const cookieDomain = (() => {
        try {
            const host = new URL(serverRuntimeConfig.DTN_APP_DOMAIN).hostname;
            if (isLocalDev(host))
                return undefined;
            return `.${host}`;
        }
        catch (_a) {
            return undefined;
        }
    })();
    return getEnvAccessToken().pipe(switchMap((data) => {
        if (!data.access_token) {
            throw new Error("Failed to get access token");
        }
        return getAuthData(data);
    }), map((data) => {
        const authInfo = data;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
        return new Auth0Client({
            domain: authInfo.data.auth0_issuer_base_url,
            // OAuth client credentials
            clientId: authInfo.data.client_id,
            clientSecret: authInfo.data.client_secret,
            // Cookie encryption secret
            secret: authInfo.data.auth0_secret,
            appBaseUrl: baseURL,
            authorizationParameters: {
                scope: "openid profile email offline_access",
                audience: audienceUrl,
            },
            // Passthrough: v4 persists all ID token claims only when this hook is provided.
            beforeSessionSaved: (session) => session,
            // Session configuration
            session: {
                cookie: Object.assign(Object.assign({}, (cookieDomain !== undefined && cookieDomain !== ""
                    ? { domain: cookieDomain }
                    : {})), { sameSite: "lax" }),
                rolling: true,
                absoluteDuration: 60 * 60 * 24 * 7,
                inactivityDuration: 60 * 60 * 24,
            },
        });
    }), catchError((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to initialize Auth0:", error);
        throw error;
    }));
};
const authInstancesByBaseUrl = new Map();
const authInstancePromises = new Map();
/* eslint-disable require-await, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
export const getAuthInstance = (req) => __awaiter(void 0, void 0, void 0, function* () {
    const baseURL = buildBaseUrlFromRequest(req);
    const existing = authInstancesByBaseUrl.get(baseURL);
    if (existing !== undefined) {
        return existing;
    }
    const pending = authInstancePromises.get(baseURL);
    if (pending !== undefined) {
        return pending;
    }
    const initPromise = firstValueFrom(initAuth(baseURL))
        .then((instance) => {
        authInstancesByBaseUrl.set(baseURL, instance);
        authInstancePromises.delete(baseURL);
        return instance;
    })
        .catch((error) => {
        authInstancePromises.delete(baseURL);
        throw error;
    });
    authInstancePromises.set(baseURL, initPromise);
    return initPromise;
});
export const auth0 = (req) => __awaiter(void 0, void 0, void 0, function* () {
    return getAuthInstance(req);
});
/**
 * Get access token from the current session.
 *
 * V4 API:
 * - Server Components: getAccessToken(options?)
 * - Pages Router/middleware: getAccessToken(req, res, options?)
 *
 * For backward compatibility, we also support the v3-style signature
 * and convert the result to include `accessToken` property.
 *
 * @param req - Request object (optional in Server Components)
 * @param res - Response object (required for Pages Router)
 * @param options - Access token options
 * @returns Promise with token info
 */
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
export const getAccessToken = (req, res, options) => __awaiter(void 0, void 0, void 0, function* () {
    const auth = yield getAuthInstance(req);
    let result;
    // V4 API has different signatures for Server Components vs Pages Router
    if (req !== undefined && res !== undefined) {
        // Pages Router / API routes: needs req and res
        result = (yield auth.getAccessToken(req, res, options));
    }
    else {
        // Server Components or request-only: use options-only signature
        result = (yield auth.getAccessToken(options));
    }
    // Add backward-compatible accessToken property
    return Object.assign(Object.assign({}, result), { accessToken: result.token });
});
/**
 * Get the current user session.
 *
 * V4 API:
 * - Server Components: getSession()
 * - Pages Router/middleware: getSession(req)
 *
 * @param req - Request object (optional in Server Components)
 * @returns Promise<Session | null> - Session data or null if not authenticated
 */
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
export const getSession = (req, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
_res) => __awaiter(void 0, void 0, void 0, function* () {
    const auth = yield getAuthInstance(req);
    // V4 API: getSession(req) for Pages Router, getSession() for Server Components
    if (req !== undefined) {
        return auth.getSession(req);
    }
    return auth.getSession();
});
/**
 * Get the Auth0 middleware handler for the App Router.
 *
 * V4: The middleware handles all auth routes automatically:
 * - /auth/login
 * - /auth/logout
 * - /auth/callback
 * - /auth/profile
 *
 * @param req - Request object
 * @returns Promise<NextResponse> - Middleware response
 */
export const getAuthMiddleware = (req) => __awaiter(void 0, void 0, void 0, function* () {
    const auth = yield getAuthInstance(req);
    return auth.middleware(req);
});
